import { buildListingAutocompleteHref } from "@/utils/ldpHref"
import { slugify } from "@/utils/slugify"
import type { CategoryNameEnum, CategoryResult } from "@/types/search"
import {
  buildMapSearchPath,
  isMapSearchPathname,
  listingIntentFromPathname,
  type SrpListingIntent,
} from "@/utils/srpListingIntent"
import { paths } from "@/app/paths"
import { embedPaths } from "./paths"

function resolveEffectivePathname(pathname: string | null): string | null {
  if (pathname) return pathname
  if (typeof window !== "undefined") return window.location.pathname
  return null
}

function resolveEffectiveSearchParams(
  searchParams: URLSearchParams | null
): URLSearchParams {
  if (searchParams) return searchParams
  if (typeof window !== "undefined") {
    return new URLSearchParams(window.location.search)
  }
  return new URLSearchParams()
}

function resolveAutocompleteListingIntent(
  pathname: string | null,
  searchParams: URLSearchParams | null,
  overrideSearchParams?: Record<string, string>
): SrpListingIntent {
  const override = overrideSearchParams?.propertySaleType
  if (override === "Rent") return "rent"
  if (override === "Sale") return "sale"

  const effectivePathname = resolveEffectivePathname(pathname)
  if (effectivePathname && isMapSearchPathname(effectivePathname)) {
    return listingIntentFromPathname(effectivePathname)
  }

  const effectiveParams = resolveEffectiveSearchParams(searchParams)
  const fromQuery = effectiveParams.get("propertySaleType")
  if (fromQuery === "Rent") return "rent"
  if (fromQuery === "Sale") return "sale"

  if (effectivePathname === paths.home()) return "sale"

  return "sale"
}

export type BuildPropertyHrefOptions = {
  pathname?: string | null
  searchParams?: URLSearchParams | null
  propertyTypes?: string[]
  sort?: string | null
  overrideSearchParams?: Record<string, string>
}

/**
 * Resolve an autocomplete result to the URL it should navigate to.
 *
 * Callers always `push`. This used to also return a `replace` flag, which erased
 * the originating page (homepage, LDP, bio) from the back stack — and it bought
 * nothing: push and replace are the same soft navigation, they differ only in
 * whether history keeps the entry you came from. Back/forward between SRPs is an
 * explicitly supported path (see `src/components/search/README.md` → *Map camera
 * ownership*), so there is no reason to collapse it.
 */
export function buildPropertyHref(
  categoryName: CategoryNameEnum,
  result: CategoryResult,
  query: string,
  options: BuildPropertyHrefOptions = {}
): string {
  const {
    pathname = null,
    searchParams = null,
    propertyTypes = [],
    sort = null,
    overrideSearchParams,
  } = options

  const effectivePathname = resolveEffectivePathname(pathname)
  const effectiveSearchParams = resolveEffectiveSearchParams(searchParams)

  if (categoryName === "Listing" && result.Address && result.BUPI) {
    const addressSlug = slugify(result.Address)
    return buildListingAutocompleteHref(query, {
      addressSlug,
      bupi: result.BUPI,
      uniqueListingId: result.ID,
      pathname: effectivePathname,
      searchParams: effectiveSearchParams,
    })
  }

  if (
    categoryName === "Address" &&
    result.DisplayName &&
    result.ID
  ) {
    return embedPaths.ldp(slugify(result.DisplayName), result.ID)
  }

  const newSearchParams = new URLSearchParams(effectiveSearchParams.toString())
  newSearchParams.delete("tl")
  newSearchParams.delete("br")
  newSearchParams.delete("topLeft")
  newSearchParams.delete("bottomRight")
  newSearchParams.delete("savedSearchName")

  if (propertyTypes.length > 0) {
    newSearchParams.set("propertyType", propertyTypes.join(","))
  }
  if (sort) {
    newSearchParams.set("sort", sort)
  }

  if (overrideSearchParams) {
    Object.entries(overrideSearchParams).forEach(([key, value]) => {
      newSearchParams.set(key, value)
    })
  }

  const listingIntent = resolveAutocompleteListingIntent(
    effectivePathname,
    effectiveSearchParams,
    overrideSearchParams
  )
  const onMapSearch = Boolean(
    effectivePathname && isMapSearchPathname(effectivePathname)
  )
  const shouldDefaultSaleTypeQuery =
    effectivePathname === paths.home() && listingIntent === "sale"

  if (onMapSearch || listingIntent === "rent") {
    newSearchParams.delete("propertySaleType")
  } else if (
    shouldDefaultSaleTypeQuery &&
    !newSearchParams.has("propertySaleType")
  ) {
    newSearchParams.set("propertySaleType", "Sale")
  }

  const slug = slugify(result.DisplayName)
  const queryString = newSearchParams.toString()
  const basePath = buildMapSearchPath(slug, listingIntent)

  return queryString.length > 0 ? `${basePath}?${queryString}` : basePath
}
